import { type ResourceIdentifier } from '@opallabs/json-api'
import type { AllSchemaKeys, ApiVersions, OpalApiComponent } from '..'

/**
 * Methods like `filter` seem to be only capable of handling a type assertion
 * function if they are provided directly and take a single argument:
 *   [].filter(isAssertingSomething) // correctly asserts type
 *   [].filter(thing => isAssertingSomething(thing)) // function argument is anonymous and cannot assert type
 *
 * Because of this, we will use a higher order function to create the type assertion function. This
 * gives us the best of both worlds, allowing us to pass in the resource type as an argument, and
 * also asserting that our Resource is a concrete type.
 *
 * Usage for filter:
 *  const onlyUserResources = jsonApiDocument.included.filter(isResourceOfType('user', 'V2'))
 *
 * Standalone usage:
 *  if (isResourceOfType('user', 'V2')(someResource)) {
 *    someResource.attributes.full_name // valid, we know its definitely a user now
 *  };
 */
export const isResourceOfType =
  <TVersion extends ApiVersions, TResourceType extends AllSchemaKeys>(
    resourceType: TResourceType,
    // Having version as a parameter allows for type inference for both arguments
    // this way the function call never needs to actually be passed the generic type args
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _version: TVersion
  ) =>
  (
    resource: unknown
  ): resource is OpalApiComponent<TVersion, TResourceType> => {
    return (
      typeof resource === 'object' &&
      resource !== null &&
      'type' in resource &&
      'id' in resource &&
      (resource as OpalApiComponent<TVersion, TResourceType>).type ===
        resourceType
    )
  }

/**
 * Similar to `isResourceOfType` above, but also takes in a specific resource `id` to lookup.
 * Built for use with Array.find.
 *
 * Usage for find:
 *  const user123 = jsonApiDocument.included.find(findResourceOfType('user', 'V2', '123'))
 *
 * Standalone usage:
 *  if (findResourceOfType('user', 'V2', '123')(someResource)) {
 *    someResource.attributes.full_name // valid, we know its definitely a user now
 *  };
 */
export const findResourceOfType =
  <TVersion extends ApiVersions, TResourceType extends AllSchemaKeys>(
    resourceType: TResourceType,
    // Having version as a parameter allows for type inference for both arguments
    // this way the function call never needs to actually be passed the generic type args
    _version: TVersion,
    resourceId: string | undefined
  ) =>
  (
    resource: unknown
  ): resource is OpalApiComponent<TVersion, TResourceType> => {
    return (
      resourceId !== undefined &&
      typeof resource === 'object' &&
      resource !== null &&
      'type' in resource &&
      'id' in resource &&
      (resource as OpalApiComponent<TVersion, TResourceType>).type ===
        resourceType &&
      (resource as OpalApiComponent<TVersion, TResourceType>).id === resourceId
    )
  }

/**
 * v3 reference to an API resource, identified by `type` and id. Can point to both v3 (`id`) and v2 (`legacyId`)
 * resources.
 */
export type ResourceRef<TType extends string = string> =
  | ResourceIdentifier<TType>
  | { type: TType; legacyId: string }

/**
 * Check if a given JSON:API resource matches the given `ref`. Built for use with `Array.find`, where it narrows the
 * result to the ref's type.
 *
 * @example
 * const moment = included.find(matchesRef({ type: 'moment', legacyId: '42' }))
 */
export const matchesRef =
  <TType extends string>(ref: ResourceRef<TType>) =>
  <TResource>(
    resource: TResource
  ): resource is Extract<TResource, { type: TType }> => {
    if (
      typeof resource !== 'object' ||
      resource === null ||
      !('type' in resource) ||
      resource.type !== ref.type
    ) {
      return false
    }

    if ('legacyId' in ref) {
      const attributes = (resource as { attributes?: { legacy_id?: string } })
        .attributes
      return attributes?.legacy_id === ref.legacyId
    }

    return 'id' in resource && resource.id === ref.id
  }
